import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Switch,
  Button,
  Space,
  Typography,
  Divider,
  Alert,
  Card,
  Row,
  Col,
  message,
  Spin,
} from 'antd';
import {
  MailOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { EmailService, EmailConfig } from '../services/emailService';

const { Title, Text, Paragraph } = Typography;

interface EmailSettingsModalProps {
  visible: boolean;
  onClose: () => void;
}

interface EmailConfigForm extends EmailConfig {
  testEmail?: string;
}

const EmailSettingsModal: React.FC<EmailSettingsModalProps> = ({
  visible,
  onClose,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'success' | 'error' | null>(null);
  const [currentConfig, setCurrentConfig] = useState<any>(null);

  useEffect(() => {
    if (visible) {
      loadCurrentConfig();
    }
  }, [visible]);

  const loadCurrentConfig = async () => {
    try {
      setConfigLoading(true);
      const config = await EmailService.getEmailConfig();
      setCurrentConfig(config);

      form.setFieldsValue({
        host: config.host,
        port: config.port,
        secure: config.secure,
      });
    } catch (error) {
      console.error('Failed to load email config:', error);
    } finally {
      setConfigLoading(false);
    }
  };

  const handleSave = async (values: EmailConfigForm) => {
    try {
      setLoading(true);

      const config: EmailConfig = {
        host: values.host,
        port: parseInt(values.port.toString(), 10),
        secure: values.secure,
        auth: {
          user: values.auth.user,
          pass: values.auth.pass,
        },
      };

      const result = await EmailService.configureEmail(config);

      if (result.success) {
        message.success('Email settings saved successfully!');
        setConnectionStatus('success');
        loadCurrentConfig();
      } else {
        message.error(result.message || 'Failed to save email settings');
        setConnectionStatus('error');
      }
    } catch (error: any) {
      console.error('Email configuration error:', error);
      message.error(error.message || 'Failed to configure email');
      setConnectionStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      const result = await EmailService.testConnection();

      if (result.success) {
        message.success('Connection test successful!');
        setConnectionStatus('success');
      } else {
        message.error(result.message || 'Connection test failed');
        setConnectionStatus('error');
      }
    } catch (error: any) {
      console.error('Connection test error:', error);
      message.error('Please save your email settings first, then test the connection.');
      setConnectionStatus('error');
    } finally {
      setTesting(false);
    }
  };

  const getCommonProviderSettings = (provider: string) => {
    switch (provider) {
      case 'gmail':
        return { host: 'smtp.gmail.com', port: 587, secure: false };
      case 'outlook':
        return { host: 'smtp-mail.outlook.com', port: 587, secure: false };
      case 'yahoo':
        return { host: 'smtp.mail.yahoo.com', port: 587, secure: false };
      default:
        return {};
    }
  };

  const applyProviderSettings = (provider: string) => {
    const settings = getCommonProviderSettings(provider);
    form.setFieldsValue(settings);
  };

  return (
    <Modal
      title={
        <Space>
          <MailOutlined />
          Email Settings
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      footer={null}
      centered
    >
      <Spin spinning={configLoading}>
        <div style={{ marginBottom: 16 }}>
          <Alert
            message="Email Configuration Setup"
            description={
              <div>
                <p style={{ marginBottom: 8 }}>Configure SMTP settings to enable email functionality for sending estimates. Your credentials are stored securely and only used for sending emails.</p>
                <p style={{ marginBottom: 0, fontWeight: 500 }}>Setup Steps: 1) Fill in SMTP settings → 2) Save Settings → 3) Test Connection</p>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        </div>

        {/* Current Status */}
        {currentConfig && (
          <Card size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Text strong>Current Configuration:</Text>
                <br />
                <Text type="secondary">Host: {currentConfig.host || 'Not configured'}</Text>
                <br />
                <Text type="secondary">Port: {currentConfig.port || 'Not configured'}</Text>
              </Col>
              <Col span={12}>
                <Text strong>Status:</Text>
                <br />
                <Space>
                  {currentConfig.hasAuth ? (
                    <>
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                      <Text type="success">Configured</Text>
                    </>
                  ) : (
                    <>
                      <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                      <Text type="warning">Needs credentials</Text>
                    </>
                  )}
                </Space>
              </Col>
            </Row>
          </Card>
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          initialValues={{
            host: 'smtp.gmail.com',
            port: 587,
            secure: false,
          }}
        >
          {/* Quick Setup */}
          <Card
            title={
              <Space>
                <SettingOutlined />
                Quick Setup
              </Space>
            }
            size="small"
            style={{ marginBottom: 16 }}
          >
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Click to apply common provider settings:
            </Text>
            <br />
            <Space style={{ marginTop: 8 }}>
              <Button size="small" onClick={() => applyProviderSettings('gmail')}>
                Gmail
              </Button>
              <Button size="small" onClick={() => applyProviderSettings('outlook')}>
                Outlook/Hotmail
              </Button>
              <Button size="small" onClick={() => applyProviderSettings('yahoo')}>
                Yahoo
              </Button>
            </Space>
          </Card>

          {/* SMTP Configuration */}
          <Title level={4}>SMTP Server Settings</Title>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="SMTP Host"
                name="host"
                rules={[{ required: true, message: 'Please enter SMTP host' }]}
                tooltip="Your email provider's SMTP server address"
              >
                <Input placeholder="smtp.gmail.com" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Port"
                name="port"
                rules={[{ required: true, message: 'Please enter port' }]}
                tooltip="SMTP port (usually 587 for TLS or 465 for SSL)"
              >
                <Input type="number" placeholder="587" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Use SSL"
                name="secure"
                valuePropName="checked"
                tooltip="Enable for port 465, disable for port 587"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          {/* Authentication */}
          <Title level={4}>Authentication</Title>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Email Address"
                name={['auth', 'user']}
                rules={[
                  { required: true, message: 'Please enter your email address' },
                  { type: 'email', message: 'Please enter a valid email address' },
                ]}
                tooltip="Your email address used for authentication"
              >
                <Input placeholder="your-email@gmail.com" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Password / App Password"
                name={['auth', 'pass']}
                rules={[{ required: true, message: 'Please enter your password' }]}
                tooltip="For Gmail, use an App Password instead of your regular password"
              >
                <Input.Password placeholder="your-app-password" />
              </Form.Item>
            </Col>
          </Row>

          {/* Gmail Instructions */}
          <Alert
            message="Gmail Users: Use App Passwords"
            description={
              <div>
                <Paragraph style={{ marginBottom: 8, fontSize: '12px' }}>
                  For Gmail accounts, you need to:
                </Paragraph>
                <ol style={{ fontSize: '12px', paddingLeft: 16, marginBottom: 0 }}>
                  <li>Enable 2-factor authentication on your Google account</li>
                  <li>Generate an App Password specifically for this application</li>
                  <li>Use the App Password instead of your regular Gmail password</li>
                </ol>
              </div>
            }
            type="warning"
            showIcon
            icon={<InfoCircleOutlined />}
            style={{ marginBottom: 16 }}
          />

          {/* Connection Status */}
          {connectionStatus && (
            <Alert
              message={connectionStatus === 'success' ? 'Connection Successful' : 'Connection Failed'}
              description={
                connectionStatus === 'success'
                  ? 'SMTP configuration is working correctly'
                  : 'Please check your settings and try again'
              }
              type={connectionStatus}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Actions */}
          <Row gutter={12} justify="end">
            <Col>
              <Button onClick={handleTestConnection} loading={testing}>
                Test Connection
              </Button>
            </Col>
            <Col>
              <Button onClick={onClose}>
                Cancel
              </Button>
            </Col>
            <Col>
              <Button type="primary" htmlType="submit" loading={loading}>
                Save Settings
              </Button>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
};

export default EmailSettingsModal;